// Best effort refresh - all invalidated cache entries will be deleted and re-downloaded.
// If network connectivity is spotty, the re-download will silently fail
import { Environment, getEnvironment } from '../../../common/src/environment.ts';
import { UrlPrefix } from '../util/path.ts';
import { localStorageGet, LocalStorageKey, localStorageRemove, localStorageSet } from './client_local_storage.ts';
import { DownloadQueueAction, DownloadQueueEntry } from './idb.ts';

// IMPORTANT: These must correspond to hymnalsDir, customMusicDir, and vocalsDir
// We cannot reference those constants b/c Babel would not be able to do certain isolated file optimizations
export enum ClientCacheName {
  Hymnals = 'hymnals',
  CustomMusic = 'custom-music',
  Vocals = 'vocals',
}

export type DownloadFunction = (url: UrlPrefix, entry: DownloadQueueEntry) => Promise<void>;

// IMPORTANT: must correspond to musicManifestFilename (not imported, for the reason above)
const manifestFilename = 'manifest.json';

function getCacheNameForUrl(url: string): ClientCacheName | undefined {
  const cacheName = url.match(/^\/(?<cacheName>[^/]+)\//)?.groups?.cacheName;
  if (!cacheName || !Object.values(ClientCacheName).includes(cacheName as ClientCacheName)) {
    // a url outside our music caches is expected (issue #3517)
    console.log(`Unrecognized cache name for ${url}`);
    return;
  }
  return cacheName as ClientCacheName;
}

export async function getCacheForUrl(url: string): Promise<Cache | undefined> {
  const cacheName = getCacheNameForUrl(url);
  if (!cacheName) {
    return;
  }
  const cache = await caches.open(cacheName);
  return cache;
}

// Thrown for a non-OK response so the download queue retries with backoff (issue #3517).
export class DownloadFailedError extends Error {
  readonly url: string;
  readonly status: number;

  constructor(url: string, status: number) {
    super(`Download failed for ${url} with status ${status}`);
    this.name = 'DownloadFailedError';
    this.url = url;
    this.status = status;
  }
}

export async function updateCacheResponse(url: string, response: Response) {
  // Keep the last good copy (issue #3517): a 404 during a re-import, or two servers momentarily
  // out of step, must not discard a working cached file. The download queue catches this and
  // retries the entry with backoff. Cache entries are deleted only by manifest-driven deletes.
  if (!response.ok) {
    throw new DownloadFailedError(url, response.status);
  }
  const cache = await getCacheForUrl(url);
  if (!cache) {
    return;
  }
  if (getEnvironment() === Environment.Dev) {
    console.log(`Updating cache entry for ${url}`);
  }
  await cache.put(url, response);
  invokeCacheListeners(url);
}

// This device's own copy of a file, read straight from the service worker's storage (Cache
// Storage) and never through fetch(). A fetch() goes through the service worker, which answers a
// miss from the network, so it cannot tell "this device has no copy" from "this device has the
// server's copy" - the mistake that made a first launch believe it was already current (issue
// #3517 follow-up). Undefined means the device has no copy.
export async function getStoredResponse(url: string): Promise<Response | undefined> {
  const cache = await getCacheForUrl(url);
  return await cache?.match(url);
}

export async function deleteUrlFromCache(url: string) {
  if (getEnvironment() === Environment.Dev) {
    console.log(`Deleting cache entry for ${url}`);
  }
  const cache = await getCacheForUrl(url);
  await cache?.delete(url);
  invokeCacheListeners(url);
}

export interface CacheRefreshInfo {
  version: number;
  cacheName: string;
  urlRegExp: string;
  downloadQueueEntry: DownloadQueueEntry;
}

// add entries to the end of this array with monotonically increasing version numbers to force client cache refresh
const cacheRefreshInfos: CacheRefreshInfo[] = [
  {
    // we had been failing to refresh custom music manifests due to an offline bug
    version: 1,
    cacheName: ClientCacheName.CustomMusic,
    urlRegExp: 'manifest.json$',
    downloadQueueEntry: {
      action: DownloadQueueAction.DeleteAndDownload,
      manifestEntry: { hash: '', isManifest: true },
    },
  },
  {
    // hymnal manifests cached before Feb 2026 still contain zero-prefixed song numbers (#3259)
    version: 2,
    cacheName: ClientCacheName.Hymnals,
    urlRegExp: 'manifest.json$',
    downloadQueueEntry: {
      action: DownloadQueueAction.DeleteAndDownload,
      manifestEntry: { hash: '', isManifest: true },
    },
  },
];

export async function applyClientCacheRefreshes(addDownload: DownloadFunction) {
  if (!navigator.onLine) {
    return;
  }
  const currentVersion = localStorageGet(LocalStorageKey.CacheRefreshVersion);
  const latestVersion = Math.max(0, ...cacheRefreshInfos.map(({ version }) => version));
  if (currentVersion === latestVersion) {
    return;
  }
  for (const refreshInfo of cacheRefreshInfos) {
    if (refreshInfo.version <= currentVersion) {
      continue;
    }
    console.log(`Applying cache refreshes: ${JSON.stringify(refreshInfo, null, 2)}`);
    await applyClientCacheRefresh(refreshInfo, addDownload);
  }
  localStorageSet(LocalStorageKey.CacheRefreshVersion, latestVersion);
}

async function applyClientCacheRefresh(
  { cacheName, urlRegExp, downloadQueueEntry }: CacheRefreshInfo,
  addDownload: DownloadFunction,
): Promise<void> {
  const cache = await caches.open(cacheName);
  for (const { url } of await cache.keys()) {
    const { pathname } = new URL(url);
    if (!pathname.match(urlRegExp)) {
      continue;
    }
    await addDownload(pathname, downloadQueueEntry);
  }
}

export async function getClientCacheUrls(cacheName: ClientCacheName) {
  const cache = await caches.open(cacheName);
  return (await cache.keys()).map(({ url }) => url);
}

const cacheListeners = new Map<string, Array<() => void>>();

export function registerCacheListener(url: string, listener: () => void) {
  cacheListeners.set(url, [...(cacheListeners.get(url) ?? []), listener]);
}

export function unregisterCacheListener(url: string, listener: () => void) {
  const listeners = cacheListeners.get(url);
  if (!listeners) {
    console.error(`Attempted to unregister cache listener for unknown URL: ${url}`);
    return;
  }
  const index = listeners.indexOf(listener);
  if (index === -1) {
    console.error(`Attempted to unregister unknown cache listener for URL: ${url}`);
    return;
  }
  listeners.splice(index, 1);
}

// On a first launch the app fetches its manifests before the service worker controls the page, so
// they come straight from the network and are never stored: the Library would be incomplete
// offline until the next launch, and the first update to any of them would be compared against
// nothing. Each manifest the app is showing right now (every one has a cache listener, see
// useFetch) that is missing from storage is queued for a refresh, which stores it and - because a
// missing manifest means "unknown" - refreshes the songs already cached under it.
export async function queueManifestsMissingFromStorage(addDownload: DownloadFunction): Promise<number> {
  let queuedCount = 0;
  for (const url of cacheListeners.keys()) {
    if (!url.endsWith(`/${manifestFilename}`) || (await getStoredResponse(url))) {
      continue;
    }
    await addDownload(url, {
      action: DownloadQueueAction.Refresh,
      manifestEntry: { hash: '', isManifest: true },
    });
    queuedCount++;
  }
  if (queuedCount > 0) {
    console.log(`Queued ${queuedCount} manifest(s) missing from offline storage`);
  }
  return queuedCount;
}

export function invokeCacheListeners(url: string) {
  const listeners = cacheListeners.get(url);
  if (listeners) {
    for (const listener of listeners) {
      listener();
    }
  }
}

export async function deleteClientCache(cacheName: ClientCacheName) {
  // trigger refresh of music caches by removing local storage keys
  if (cacheName === ClientCacheName.Hymnals) {
    localStorageRemove(LocalStorageKey.HymnalsHash);
  } else if (cacheName === ClientCacheName.CustomMusic) {
    localStorageRemove(LocalStorageKey.CustomMusicHash);
  }

  for (const url of cacheListeners.keys()) {
    if (getCacheNameForUrl(url) === cacheName) {
      invokeCacheListeners(url);
    }
  }
  await caches.delete(cacheName);
}

interface CacheStorageEntryInfo {
  url: string;
  mimeType: string;
  sizeMB: number;
}

export interface CacheStorageInfo {
  name: ClientCacheName;
  count: number;
  sizeMB: number;
  entries: CacheStorageEntryInfo[];
}

// NOTE: Very expensive function and should only be called at the point the results are used, never in the background
export async function getCacheStorageInfo(): Promise<CacheStorageInfo[]> {
  return await Promise.all(
    (await caches.keys()).map(async (name) => {
      const cache = await caches.open(name);
      const requests = await cache.keys();
      let sizeMB = 0;
      const requestInfos: CacheStorageEntryInfo[] = [];
      for (const request of requests) {
        const response = await cache.match(request);
        if (response && response.status === 200) {
          const blob = await response.clone().blob();
          const blobSizeMB = blob.size / 1000000;
          sizeMB += blobSizeMB;
          requestInfos.push({ url: request.url, mimeType: blob.type, sizeMB: blobSizeMB });
        }
      }
      return { name: name as ClientCacheName, count: requests.length, sizeMB, entries: requestInfos };
    }),
  );
}

export async function getCachedResponse(url: string) {
  const cache = await getCacheForUrl(url);
  if (!cache) {
    console.error(`Unable to find cache for url ${url}`);
    return;
  }
  const response = await cache.match(url);
  if (!response) {
    console.error(`Unable to find response for ${url}`);
  }
  return response;
}

export async function putCachedResponse(url: string, response: Response) {
  const cache = await getCacheForUrl(url);
  if (!cache) {
    console.error(`Unable to find cache for url ${url}`);
    return;
  }
  await cache.put(url, response);
  invokeCacheListeners(url);
}
